import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  ContentChild,
  TemplateRef,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core'

type KnobType = 'circle' | 'vertical_rectangle' | 'horizontal_rectangle' | 'donut'

export class DonutSlice {
  percent: number
  color: string
}

export class DonutConfig {
  width: string = '135'
  height: string = '150'
  title?: string
  subTitle?: string
  slice: DonutSlice[]
}

@Component({
  selector: 'app-myknob',
  templateUrl: './myknob.component.html',
  styleUrls: ['./myknob.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KnobGaugeComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ContentChild(TemplateRef) inside: TemplateRef<unknown> | null

  @ViewChild('doughnutKnob', { static: false })
  doughnutKnob: ElementRef<HTMLCanvasElement>

  /** Percent number */
  @Input() percent: number = 0

  /** Knob size */
  @Input() size: number = 128

  @Input() status: string = 'def'

  /** Knob type by default is circle */
  @Input() type: KnobType = 'circle'

  /** Data use when the type is donut */
  @Input() data: DonutConfig

  /** View box sizer by default is 120 */
  @Input() viewBox = 120

  /** Total value use when the type is  "horizontal_rectangle" only*/
  @Input() totalValue?: unknown

  /** Use component inside component? */
  @Input() useComponentInside: boolean

  /** Additional classes for the knob container content */
  @Input() contentContainerStyleClass: string

  /** Donut knob on click output */
  @Output() doughnutKnobOnClick = new EventEmitter()

  /** Radius */
  r: number = 54

  classList: Record<string, boolean> = {}

  percentToShow: number = 0


  ngOnInit(): void {
    if (this.percent) {
      this.classList[this.status] = true
    }

    let val = this.percent
    const r = this.r
    const c = Math.PI * (r * 2)
    if (val < 0) {
      val = 0
    }
    if (val > 100) {
      val = 100
    }
    const pct = ((100 - val) / 100) * c
    this.percentToShow = pct
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.doughnutKnob) {
      this.drawDoughnutKnob()
    }
  }

  ngAfterViewInit(): void {
    if (this.type == 'donut') {
      // Call the function to draw the doughnut knob
      this.drawDoughnutKnob()
    }
  }

  /**
   * Draw the doughnut knob
   */
  drawDoughnutKnob() {
    const canvas = this.doughnutKnob.nativeElement
    const context = canvas.getContext('2d')!
    const centerX = canvas.width / 2
    const centerY = canvas.height / 2

    const outerRadius = 54 // Adjust outer circle radius
    const gapWidth = this.data.slice?.length > 1 ? 0.3 : 0 // Adjust the gap between segments
    const segmentColors = this.data?.slice?.length ? this.data.slice.map((v) => v.color) : ['#ecebed']

    context.clearRect(0, 0, canvas.width, canvas.height)

    // Draw outer circle segments
    const numSegments = segmentColors.length
    const totalGapWidth = gapWidth * numSegments
    const totalSegmentAngle = 2 * Math.PI - totalGapWidth

    let lastSegment = 0
    for (let i = 0; i < numSegments; i++) {
      const segmentAngle = this.data.slice?.length > 1 ? (totalSegmentAngle * this.data.slice[i].percent) / 100 : totalSegmentAngle

      const startAngle = this.data.slice?.length > 1 ? lastSegment : 0

      const endAngle = this.data.slice?.length > 1 ? segmentAngle + startAngle : totalSegmentAngle
      lastSegment = endAngle + gapWidth

      context.beginPath()
      context.arc(centerX, centerY, outerRadius, startAngle, endAngle)
      context.lineCap = 'round'

      context.strokeStyle = segmentColors[i]
      context.lineWidth = 10
      context.stroke()
    }

    // Add text in the center
    if (this.data.title) {
      context.fillStyle = '#000'
      context.font = 'bold 32px Assistant, sans-serif'
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      context.fillText(this.data.title, centerX, centerY - 12)
    }

    if (this.data.subTitle) {
      const subTitleWords = this.data.subTitle.split(' ') // Split the subtitle into an array of words
      context.fillStyle = 'rgba(57,58,76,.5)'
      context.font = '14px Assistant, sans-serif'
      context.textAlign = 'center'
      context.textBaseline = 'middle'

      const subTitleLineHeight = 20 // Adjust line height as needed
      const subTitleInitialY = centerY + 12 // Starting Y position

      subTitleWords.forEach((word, index) => {
        const yPosition = subTitleInitialY + index * subTitleLineHeight
        context.fillText(word, centerX, yPosition)
      })
    }
  }

  get horizontalRectangleStyle() {
    return { width: this.percent + '%' }
  }

  ngOnDestroy() {
    // Clean up any template references
    if (this.inside) {
      this.inside = null
    }
  }
}
